import $ from '../../../../../FrontendBundle/Resources/js/jquery';
import { OfcsObject } from '../../../../../FrontendBundle/Resources/js/ofcs';

/**
 * @class AbstractField
 */
export default class AbstractField extends OfcsObject {
    type = 'abstract';
    supports = null;

    /**
     * `<div/>` container of field
     *
     * @property {jQuery} control
     */

    /**
     * Tooltip Template
     *
     * @property {jQuery} tooltipTemplate
     */

    /**
     * Currently selected item.
     *
     * @property {ItemInterface|null} currentItem
     */

    /**
     * Length of selection.
     *
     * @property {Number} selectionLength
     */

    /**
     * Global editor object.
     *
     * @property {Ofcs.Editor} editor
     */

    /**
     * Translations
     *
     * @property {Object} translations
     */

    /**
     * Selection parameters
     *
     * @property {Object} selectionParameters
     */

    /**
     * Initializes the field
     *
     * @param {jQuery} control `<div/>` container of field
     * @param {Object} translations
     */
    constructor(control, translations) {
        super();
        this.tooltipTemplate = $($.fn.tooltip.defaults.template).addClass('error');

        this.currentItem = null;
        this.selectionLength = 0;
        this.selectionParameters = {};
        this.hasError = false;
        this.editor = null;

        this.control = control;
        this.isHidden = true;

        this.translations = translations;

        this.assertElementIsUnique(control, this.type, 'control');
    }

    /**
     * Assert a single elemnt is selected.
     *
     * @param {jQuery} element
     * @param {string} name
     * @param {string} type
     */
    assertElementIsUnique(element, name, type) {
        if (!(element instanceof $)) {
            throw new Error(`Element "${type}:${name}" is not a jQuery element.`);
        }

        if (element.length !== 1) {
            throw new Error(`Element "${type}:${name}" is not unique, found ${element.length}.`);
        }
    }

    /**
     * Try to update the item. If validation fails an error toolip is shown.
     *
     * @param {ItemInterface} item
     * @param {number} difference
     */
    tryUpdateItem(item, difference) {
        try {
            if (difference) {
                this.updateItemRelative(item, difference);
            } else {
                this.updateItem(item);
            }
        } catch (msg) {
            this.showError(msg);
        }
    }

    /**
     * Set visibility of the field by item.
     *
     * @param {ItemInterface} item
     * @param {number} selectionLength
     */
    setVisibilityForSelection(item, selectionLength) {
        if (this.isSupported(item, selectionLength)) this.show();
        else this.hide();
    }

    /**
     * Hide the field.
     */
    hide() {
        this.control.hide();
        this.isHidden = true;
    }

    /**
     * Show the field.
     */
    show() {
        this.control.show();
        this.isHidden = false;
    }

    /**
     * Update field with item values.
     *
     * @param {ItemInterface} item
     * @param {number} selectionLength
     */
    update(item, selectionLength) {
        if (this.isSupported(item, selectionLength)) {
            this.updateValue(item);
            return;
        }

        this.hide();
    }

    /**
     * Do action.
     *
     * @param {ItemInterface} item
     * @param {Object} parameters
     */
    action(item, parameters) { // eslint-disable-line no-unused-vars
    }

    /**
     * Check if item supports field.
     *
     * @param {ItemInterface} item
     * @param {number} selectionLength
     *
     * @return {boolean}
     */
    isSupported(item, selectionLength) {
        const supportFlag = this.supports;

        return selectionLength === 1 && supportFlag && item && item[supportFlag];
    }

    /**
     * Show an error tooltip for the field.
     *
     * @param {Error|string} error
     */
    showError(error) {
        let title = error;

        if (error instanceof Error) {
            if (console) {
                console.log(`Message: ${error.message}`); // eslint-disable-line no-console
                console.log(`Stacktrace: ${error.stack}`); // eslint-disable-line no-console
            }

            title = `Error occured: ${error.message}`;
        }

        const placement = ($('html').hasClass('layout-flat') && $('html').hasClass('editor-page'))
            ? 'bottom'
            : 'top';

        this.control
            .addClass('error')
            .tooltip('hide')
            .children(':first')
            .tooltip({
                title,
                trigger: 'manual',
                template: this.tooltipTemplate,
                placement,
            })
            .tooltip('show');

        this.hasError = true;
    }

    /**
     * Hide the error tooltip.
     */
    hideError() {
        if (this.hasError) {
            this.control
                .removeClass('error')
                .tooltip('hide')
                .children(':first')
                .tooltip('hide')
                .tooltip('destroy');

            this.hasError = false;
        }
    }

    /**
     * Validate an item value.
     *
     * @param {ItemInterface} item
     * @param {string} attribute
     * @param {*} value
     *
     * @throws Error message.
     */
    assertItemValue(item, attribute, value) {
        const data = {
            [attribute]: value,
        };

        const errors = item.validate(data);

        if (errors && errors[attribute] && errors[attribute].tpl) {
            const template = this.translations[errors[attribute].tpl];

            throw new Error(template({ value: errors[attribute].value }));
        }
    }

    /**
     * Set the current active item.
     *
     * @param {ItemInterface} item
     * @param {number} selectionLength
     * @param {Object} selectionParameters
     */
    setSelection(item, selectionLength, selectionParameters = {}) {
        this.currentItem = item;
        this.selectionLength = selectionLength;
        this.selectionParameters = selectionParameters;
    }

    /**
     * Set editor object.
     *
     * @param {Ofcs.Editor} editor
     */
    setEditor(editor) {
        this.editor = editor;
    }
}
